<script setup lang="ts">
import { refDebounced, watchDebounced, useElementBounding } from '@vueuse/core'
import axios from 'axios'
import { trim } from 'lodash-es'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import onKeyboardShortcut from '~/composables/onKeyboardShortcut'
import useErrorHandler from '~/composables/useErrorHandler'
import { useStore } from '~/store'
import { useModal } from '~/shared/useModal'
import { getKey, useDataStore, DataPagination } from '~/data'

import type { components, paths } from '~/generated/types.d.ts'
import AlbumCard from '~/components/album/Card.vue'
import ArtistCard from '~/components/artist/Card.vue'
import ChannelCard from '~/components/audio/ChannelCard.vue'
import TrackTable from '~/components/audio/track/Table.vue'
import EmptyState from '~/components/common/EmptyState.vue'
import ActorCard from '~/components/federation/ActorCard.vue'
import PlaylistCard from '~/components/playlists/Card.vue'
import RadioButton from '~/components/radios/Button.vue'
import RadioCard from '~/components/radios/Card.vue'
import TagsList from '~/components/tags/List.vue'

import { to } from '~/routing'

import Alert from '~/ui/Alert.vue'
import Card from '~/ui/Card.vue'
import Input from '~/ui/Input.vue'
import Loader from '~/ui/Loader.vue'
import Modal from '~/ui/Modal.vue'
import Pagination from '~/ui/Pagination.vue'
import Section from '~/ui/Section.vue'
import Spacer from '~/ui/Spacer.vue'
import Link from '~/ui/Link.vue'
import CursorPagination from '~/ui/CursorPagination.vue'

const { t } = useI18n()

const globalSearchInput = useTemplateRef('globalSearchInput')

const focusSearch = () => {
  globalSearchInput.value?.focus();
}

onKeyboardShortcut(['shift', 'f'], focusSearch, true)
onKeyboardShortcut(['ctrl', 'k'], focusSearch, true)
onKeyboardShortcut(['/'], focusSearch, true)

const { isOpen, value: query } = useModal(
  'search', {
    on: () => '',
    isOn: value => value !== undefined && value !== ''
  }
)

// Search query

const queryDebounced = refDebounced(query, 400)
const trimmedQuery = computed(() => trim(trim(queryDebounced.value), '@'))
const isRss = computed(() => trimmedQuery.value.includes('.rss') || trimmedQuery.value.includes('.xml'))
const isFetch = computed(() => ((trimmedQuery.value.startsWith('http://') || trimmedQuery.value.startsWith('https://')) || trimmedQuery.value.includes('@')) && !isRss.value)

const isLoading = ref(false)

// Search input sizing
const outerRef = useTemplateRef('outer')
const innerRef = useTemplateRef('inner')

const outerBounds = useElementBounding(outerRef)
const innerBounds = useElementBounding(innerRef)

const searchStyle = computed(() => {
  const b = isOpen.value ? innerBounds : outerBounds

  // Guard against the split-second where the element might be unmounted (0 width)
  if (b.width.value === 0) return ''

  return `left: ${b.left.value}px; top: ${b.top.value}px; width: ${b.width.value}px;`
})

watch(isOpen, (newVal) => {
  setTimeout(() => {
    if (newVal) innerBounds.update()
    else outerBounds.update()
  }, 300)

  setTimeout(() => {
    if (newVal) innerBounds.update()
    else outerBounds.update()
  }, 50)

  setTimeout(() => {
    if (newVal) innerBounds.update()
    else outerBounds.update()
  }, 100)
})

// RSS and Federation (remote object) search

type Category = 'rss' | 'federation'

type Response = {
  rss: paths['/api/v2/channels/rss-subscribe/']['post']['responses']['200']['content']['application/json']
  federation: paths['/api/v2/federation/fetches/']['post']['responses']['201']['content']['application/json']
}

type Results = {
  rss: [Response['rss']]
  federation: [Response['federation']]
  type: Category
} // Note that `federation` is a singleton list so that each result is a list

const responses = ref<Partial<Response>>({})
const results = ref<Partial<Results>>({})

const categories = computed(() => [
  {
    type: 'rss',
    label: t('views.Search.header.rss'),
    endpoint: '/channels/rss-subscribe/',
    post: true,
    params: {
      url: trimmedQuery.value
    }
  },
  {
    type: 'federation',
    label: t('views.Search.header.remote'),
    endpoint: '/federation/fetches/',
    post: true,
    params: {
      object_uri: trimmedQuery.value
    }
  }
] as const satisfies {
  type: Category
  label: string
  post?: true
  params?: {
    [key: string]: string | number
  }
  endpoint: `/${string}`
}[])

/**
 * Limit the available categories based on the search query
 * Show fetch if the query is a URL; show RSS if the query is an email address; show all other cateories otherwise
 */
const availableCategories = computed(() =>
  categories.value.filter(({ type }) =>
    isFetch.value ? type === 'federation'
      : isRss.value ? type === 'rss'
        : type !== 'federation' && type !== 'rss'
  )
)

/**
 * Get a list of the loaded results for a given category (max. 4)
 * @param category The category to get the results for
 * @returns The results for the given category, in the form of an Array; `[]` if the category has not yet been queried
 */
const resultsPerCategory = <C extends Category>(category: { type: C }) =>
  results.value[category.type] || []

/**
 * Get the total number of results
 * @param category The category to get the results for
 * @returns The number of results for the given category according to the backend; `0` if the category has not yet been queried
 */
const count = <C extends Category>(category: { type: C }) => (
  response => response && 'count' in response ? response.count : resultsPerCategory(category).length
)(responses.value[category.type])

// Subscribe to an RSS feed

const store = useStore()

const rssError = ref()

/**
 * Subscribe to an RSS feed and return the route for the subscribed channel
 * @param url The RSS feed URL
 * @returns The route object for the subscribed channel
 */
const rssSubscribe = async (url: string) => {
  try {
    const response = await axios.post('channels/rss-subscribe/', { url })
    store.commit('channels/subscriptions', { uuid: response.data.channel.uuid, value: true })
    return response.data.channel
  } catch (error) {
    // @ts-expect-error TODO: type this correctly
    rssError.value = [error.message, ...error.backendErrors].join(' – ')
    useErrorHandler(error as Error)
    return null
  }
}

// Federated Search

const federationError = ref()

const search = async () => {
  // We are searching for RSS or for federated objects only. Fetches for other objects are handled by rstore.
  // TODO: Now that we use imperative axios calls only for the POST operations, the abstraction
  // into a list makes no sense any more. We'll see how the backend develops (GET for rss and fetches),
  // and the rstore plugin will support POST, PATCH etc. at some point
  if (availableCategories.value.length === 0) return;

  // Close if query is empty
  if (trimmedQuery.value.length < 1) {
    isOpen.value = false
    return
  }

  // Only query category that are open / available. Omit duplicate queries (`uniqBy`).

  isLoading.value = true

  for (const category of availableCategories.value) {
    try {
      // TODO: add (@)type key to Response type
      if (category.type === 'rss') {
        const channel = await rssSubscribe(trimmedQuery.value)
        if (channel) {
          results.value.rss = [channel] // Store the subscribed channel
        }
      } else if (category.type === 'federation') {
        const response = await axios.post<Response['federation']>(
          category.endpoint,
          { object_uri: trimmedQuery.value }
        )
        results.value.type = category.type
        results.value.federation = [response.data]
        responses.value[category.type] = response.data

        // Interesting: If a federation url is empty, the request returns the local user, but with status errored.

        if (response.data.status === 'errored') {
          // @ts-expect-error TODO: type this correctly
          federationError.value = response?.data?.detail?.message
        }
      }
    } catch (error) {
      // @ts-expect-error TODO: type this correctly
      federationError.value = [error?.message, ...(error?.backendErrors || [])].join(' – ')
      useErrorHandler(error as Error)
    }
  }
  isLoading.value = false
}

// Global force-refresh signal

const generation = ref(0)

// Start the rss and federation search

watchDebounced(queryDebounced, search, {
  debounce: 1000, maxWait: 10000, immediate: true
})

// Pagination
const artistsCursor = ref<string>()
const albumsCursor = ref<string>()
const tracksPage = ref(1)
const tagsPage = ref(1)
const playlistsPage = ref(1)
const radiosPage = ref(1)
const podcastsCursor = ref<string>()
const seriesCursor = ref<string>()

// Reset all pagination pages to 1 when the trimmed query changes
watchDebounced(trimmedQuery, () => {
  artistsCursor.value = undefined
  albumsCursor.value = undefined
  tracksPage.value = 1
  tagsPage.value = 1
  playlistsPage.value = 1
  radiosPage.value = 1
  podcastsCursor.value = undefined
  seriesCursor.value = undefined
}, { debounce: 50 })
</script>

<template>
  <div
    ref="outer"
    class="secondary raised interactive solid" :class="[$style.placeholder]"
  />
  <Modal
    v-model="isOpen"
    autofocus="off"
    title=""
    maximize-size
    :priority="12"
  >
    <template #topleft>
      <!-- The following is a placeholder for the original input element and contributes only its position and dimensions -->
      <div
        ref="inner"
        ui-raised
        class="color-default-solid" :class="[$style.placeholder]"
      />
    </template>

    <!-- Federation (Fetch) -->

    <template
      v-if="isFetch"
      #default
    >
      <Spacer size-32 />
      <Section
        class="search-section"
        :columns-per-item="3"
        align-left
      >
        <Card
          class="color-destructive-solid"
          :title="t('views.Search.header.remote')"
        >
          <template
            v-if="federationError"
            #alert
          >
            {{ federationError }}
          </template>
          <Loader v-if="isLoading" />
          <EmptyState
            v-else-if="count({ type: 'federation' }) === 0"
            refresh
            @refresh="search"
          />
        </Card>
        <template
          v-for="result in resultsPerCategory({ type: 'federation' })"
          :key="result.id"
        >
          <!-- TODO: use a different endpoint for remote user search with FullActor to get user avatars -->
          <ActorCard
            v-if="result.object && result.type === 'account'"
            :actor="result.object as components['schemas']['Actor']"
          />
          <ChannelCard
            v-else-if="result.object && result.type === 'channel'"
            :object="result.object as components['schemas']['Channel']"
          />
          <ArtistCard
            v-else-if="result.object && result.type === 'artist'"
            :artist="result.object as components['schemas']['ArtistWithAlbums']"
          />
          <AlbumCard
            v-else-if="result.object && result.type === 'album'"
            :album="result.object as components['schemas']['Album']"
          />
          <PlaylistCard
            v-else-if="result.object && result.type === 'playlist'"
            :playlist="result.object as components['schemas']['Playlist']"
          />
          <TrackTable
            v-else-if="result.object && result.type === 'track'"
            :tracks="[result.object] as components['schemas']['Track'][]"
          />
        </template>
      </Section>
    </template>

    <!-- RSS -->

    <template
      v-else-if="isRss"
      #default
    >
      <ChannelCard
        v-if="results.rss && results.rss[0]"
        :key="results.rss[0].uuid"
        :object="results.rss[0]"
      />
      <Card
        v-if="rssError"
        full
        red
        :title="t('views.Search.header.rss')"
      >
        <template #alert>
          {{ rssError }}
        </template>
      </Card>
    </template>

    <!-- No RSS or Fetch -->

    <template
      v-else
      #default="{ cardsPerRow }"
    >
      <Spacer size-32 />

      <!-- Artists -->

      <template
        v-for="{ value: { status, data, error, key } } in [useDataStore().get_artists({
          q: query,
          cursor: artistsCursor,
          page_size: cardsPerRow() * 2 - 1,
        }, { deduplicationKey: `search.${generation}.artists` })]"
        :key
      >
        <Section
          v-if="(data?.results?.length ?? 0) > 0"
          class="search-section"
          icon="bi:person"
          :h1="t('artist' /* https://dev.funkwhale.audio/funkwhale/funkwhale/-/work_items/2730#note_81984 */)"
          :columns-per-item="3"
          align-left
        >
          <Loader
            v-if="status === 'loading'"
          />

          <Alert
            v-if="status === 'error'" class="color-red-solid"
            style="align-items: center; grid-column: 1 / -1;"
          >
            {{ error }}
          </Alert>
          <template #action>
            <RadioButton
              type="custom_multiple"
              :radio-config="[{
                type: 'artist',
                ids: (data?.results ?? []).map(({ id }) => id)
              }]"
            />
          </template>

          <ArtistCard
            v-for="(artist) in data?.results"
            :key="getKey(artist)"
            :artist
          />

          <Link
            v-bind="to({ name: 'library.artists.browse', query: { query } })"
            style="grid-column: -3 / -1; justify-self: end;"
          >
            <Icon icon="bi:search" />
            {{ t('views.Search.label.artists') }}
          </Link>

          <DataPagination
            :is="CursorPagination"
            v-model="artistsCursor"
            :resource="{ data }"
          />
        </Section>
        <Spacer
          v-if="(data?.results.length ?? 0) > 0"
          size-64
        />
      </template>

      <!-- Albums -->

      <template
        v-for="{ value: { status, data, error, key } } in [useDataStore().get_albums({
          q: query,
          cursor: albumsCursor,
          page_size: cardsPerRow() * 2 - 1,
        }, { deduplicationKey: `search.${generation}.albums` })]"
        :key
      >
        <Section
          v-if="(data?.results?.length ?? 0) > 0"
          class="search-section"
          icon="bi:disc"
          :h1="t('album', /* https://dev.funkwhale.audio/funkwhale/funkwhale/-/work_items/2730#note_81984 */)"
          :columns-per-item="3"
          align-left
        >
          <Alert
            v-if="status === 'error'" class="color-red-solid"
            style="align-items: center; grid-column: 1 / -1;"
          >
            {{ error }}
          </Alert>
          <Loader v-if="status === 'loading'" />

          <AlbumCard
            v-for="(album) in data?.results"
            :key="getKey(album)"
            :album
          />

          <Link
            v-bind="to({ name: 'library.albums.browse', query: { query } })"
            style="grid-column: -3 / -1; justify-self: end;"
          >
            <Icon icon="bi:search" />
            {{ t('views.Search.label.albums') }}
          </Link>

          <DataPagination
            :is="CursorPagination"
            v-model="albumsCursor"
            :resource="{ data }"
          />
        </Section>
        <Spacer
          v-if="(data?.results.length ?? 0) > 0"
          size-64
        />
      </template>

      <!-- Tracks -->

      <template
        v-for="{ value: { status, data, error, key } } in [useDataStore().get_tracks({
          q: query,
          page: tracksPage,
          page_size: cardsPerRow() * 2 - 1,
        }, { deduplicationKey: `search.${generation}.tracks` })]"
        :key
      >
        <Section
          v-if="(data?.count ?? 0) > 0"
          class="search-section"
          icon="bi:music-note-beamed"
          :h1="t('tracks', data?.count || 0)"
          :columns-per-item="3"
          align-left
        >
          <Alert
            v-if="status === 'error'"
            class="color-red-solid"
            style="align-items: center; grid-column: 1 / -1;"
          >
            {{ error }}
          </Alert>
          <Loader
            v-if="status === 'loading'"
          />
          <TrackTable
            :tracks="data?.results"
            style="grid-column: 1 / -1"
          />

          <Pagination
            v-if="(data?.count ?? 0) > (cardsPerRow() * 2 - 1)"
            v-model:page="tracksPage"
            style="grid-column: 1 / -1;"
            :pages="Math.ceil((data?.count ?? 0) / (cardsPerRow() * 2 - 1))"
          />
        </Section>
        <Spacer
          v-if="(data?.count ?? 0) > 0"
          size-64
        />
      </template>

      <!-- Tags -->

      <template
        v-for="{ value: { status, data, error, key } } in [useDataStore().get_tags({
          q: query,
          page: tagsPage,
          page_size: 20,
        }, { deduplicationKey: `search.${generation}.tags` })]"
        :key
      >
        <Section
          v-if="(data?.count ?? 0) > 0"
          class="search-section"
          icon="bi:tags"
          :h1="t('tags', data?.count || 0)"
          :columns-per-item="3"
          align-left
        >
          <Alert
            v-if="status === 'error'"
            class="color-red-solid"
            style="align-items: center; grid-column: 1 / -1;"
          >
            {{ error }}
          </Alert>
          <Loader v-if="status === 'loading'" />

          <template #action>
            <RadioButton
              type="custom_multiple"
              :radio-config="[{
                type: 'tag',
                names: (data?.results ?? []).map(({ name }) => name)
              }]"
            />
          </template>

          <TagsList
            style="grid-column: 1 / -1"
            :truncate-size="200"
            :limit="20"
            :tags="data?.results?.map(t => t.name) || []"
            ui-indicate-overflow
          />

          <!-- TODO: Is there a page for browsing tags? library.tags.browse does not exist. -->
          <!-- <Link
            v-bind="to({ name: 'library.tags.browse', query: { query } })"
          >
            <Icon icon="bi:search" />
            {{ t('views.Search.label.tags') }}
          </Link> -->

          <Pagination
            v-if="(data?.count ?? 0) > 20"
            v-model:page="tagsPage"
            style="grid-column: 1 / -1;"
            :pages="Math.ceil((data?.count ?? 0) / 20)"
          />
        </Section>
        <Spacer
          v-if="(data?.count ?? 0) > 0"
          size-64
        />
      </template>

      <!-- Playlists -->

      <template
        v-for="{ value: { status, data, error, key } } in [useDataStore().get_playlists({
          q: query,
          page: playlistsPage,
          page_size: cardsPerRow() * 2 - 1,
        }, { deduplicationKey: `search.${generation}.playlists` })]"
        :key
      >
        <Section
          v-if="(data?.count ?? 0) > 0"
          class="search-section"
          icon="bi:music-note-list"
          :h1="t('playlists', data?.count || 0)"
          :columns-per-item="3"
          align-left
        >
          <Alert
            v-if="error" class="color-red-solid"
            style="align-items: center; grid-column: 1 / -1;"
          >
            {{ error }}
          </Alert>
          <Loader v-if="status === 'loading'" />
          <template #action>
            <RadioButton
              type="custom_multiple"
              :radio-config="[{
                type: 'playlist',
                uuids: (data?.results ?? []).map(({ uuid }) => uuid)
              }]"
            />
          </template>

          <PlaylistCard
            v-for="(playlist) in data?.results || []"
            :key="getKey(playlist)"
            :playlist
          />

          <Link
            v-bind="to({ name: 'library.playlists.browse', query: { query } })"
            style="grid-column: -3 / -1; justify-self: end;"
          >
            <Icon icon="bi:search" />
            {{ t('views.Search.label.playlists') }}
          </Link>

          <Pagination
            v-if="(data?.count ?? 0) > (cardsPerRow() * 2 - 1)"
            v-model:page="playlistsPage"
            style="grid-column: 1 / -1;"
            :pages="Math.ceil((data?.count ?? 0) / (cardsPerRow() * 2 - 1))"
          />
        </Section>
        <Spacer
          v-if="(data?.count ?? 0) > 0"
          size-64
        />
      </template>

      <!-- Radios -->

      <template
        v-for="{ value: { status, data, error, key } } in [useDataStore().get_radios({
          q: query,
          page: radiosPage,
          page_size: cardsPerRow() * 2 - 1,
        }, { deduplicationKey: `search.${generation}.radios` })]"
        :key
      >
        <Section
          v-if="(data?.count ?? 0) > 0"
          class="search-section"
          icon="bi:boombox-fill"
          :h1="t('radios', data?.count || 0)"
          :columns-per-item="3"
          align-left
        >
          <Alert
            v-if="error" class="color-red-solid"
            style="align-items: center; grid-column: 1 / -1;"
          >
            {{ error }}
          </Alert>
          <Loader v-if="status === 'loading'" />

          <RadioCard
            v-for="(radio) in data?.results || []"
            :key="getKey(radio)"
            type="custom"
            :custom-radio="radio"
          />

          <Pagination
            v-if="(data?.count ?? 0) > (cardsPerRow() * 2 - 1)"
            v-model:page="radiosPage"
            style="grid-column: 1 / -1;"
            :pages="Math.ceil((data?.count ?? 0) / (cardsPerRow() * 2 - 1))"
          />

          <Link
            v-bind="to({ name: 'library.radios.browse', query: { query } })"
            style="grid-column: -3 / -1; justify-self: end;"
          >
            <Icon icon="bi:search" />
            {{ t('views.Search.label.radios') }}
          </Link>
        </Section>
        <Spacer
          v-if="(data?.count ?? 0) > 0"
          size-64
        />
      </template>

      <!-- Podcasts -->

      <template
        v-for="{ value: { status, data, error, key } } in [useDataStore().get_artists({
          q: query,
          content_category: 'podcast',
          include_channels: true,
          cursor: podcastsCursor,
          page_size: cardsPerRow() * 2 - 1,
        }, { deduplicationKey: `search.${generation}.podcasts` })]"
        :key
      >
        <Section
          v-if="(data?.results.length ?? 0) > 0"
          class="search-section"
          icon="bi:boombox-fill"
          :h1="t('podcasts', data?.results.length || 0) /* TODO: Remove or refactor! */"
          :columns-per-item="3"
          align-left
        >
          <Alert
            v-if="error" class="color-red-solid"
            style="align-items: center; grid-column: 1 / -1;"
          >
            {{ error }}
          </Alert>
          <Loader v-if="status === 'loading'" />

          <ArtistCard
            v-for="(artist) in data?.results"
            :key="getKey(artist)"
            :artist
          />

          <Link
            v-bind="to({ name: 'library.podcasts.browse', query: { query } })"
            style="grid-column: -3 / -1; justify-self: end;"
          >
            <Icon icon="bi:search" />
            {{ t('views.Search.label.podcasts') }}
          </Link>

          <DataPagination
            :is="CursorPagination"
            v-model="podcastsCursor"
            :resource="{ data }"
          />
        </Section>
        <Spacer
          v-if="(data?.results.length ?? 0) > 0"
          size-64
        />
      </template>

      <!-- Series -->

      <template
        v-for="{ value: { status, data, error, key } } in [useDataStore().get_albums({
          q: query,
          content_category: 'podcast',
          include_channels: true,
          cursor: seriesCursor,
          page_size: cardsPerRow() * 2 - 1,
        }, { deduplicationKey: `search.${generation}.series` })]"
        :key
      >
        <Section
          v-if="(data?.results.length ?? 0) > 0"
          class="search-section"
          :h1="t('podcast series', data?.results.length || 0) /* TODO: Remove or refactor! */"
          icon="bi:collection-play"
          :columns-per-item="3"
          align-left
        >
          <Alert
            v-if="error" class="color-red-solid"
            style="align-items: center; grid-column: 1 / -1;"
          >
            {{ error }}
          </Alert>
          <Loader v-if="status === 'loading'" />

          <AlbumCard
            v-for="(album) in data?.results"
            :key="getKey(album)"
            :album
          />

          <DataPagination
            :is="CursorPagination"
            v-model="seriesCursor"
            :resource="{ data }"
          />
        </Section>
        <Spacer
          v-if="(data?.results.length ?? 0) > 0"
          size-64
        />
      </template>

      <!-- Global indicator -->

      <template v-if="useDataStore().numberOfLoadingResources > 0 || useDataStore().numberOfQueuedResources > 0 || isLoading">
        <Spacer size-64 />
        <Loader v-if="isLoading" />
      </template>
      <EmptyState
        v-else
        :class="$style.hideIfAfterSection"
        :refresh="true"
        @refresh="() => { search(); generation++ }"
      />
    </template>
  </Modal>

  <!-- Global search input (either in sidebar or on modal - follows the placeholder) -->

  <Teleport to="body">
    <div
      :style="searchStyle"
      :class="$style.input"
    >
      <Input
        ref="globalSearchInput"
        v-model="query"
        class="shape-field-full"
        ui-sunken
        autocomplete="search"
        type="search"
        icon="bi:search"
        :placeholder="t('components.audio.SearchBar.placeholder.search')"
        :autofocus="isOpen || undefined"
      >
        <template #start>
          <Icon icon="bi:search" />
        </template>
      </Input>
    </div>
  </Teleport>
</template>

<style module>
  :global(.search-section)~.hideIfAfterSection {
    display: none;
  }
.placeholder {
    position: relative;
    flex-grow: 1;
    border: none;
    border-radius: 8px;
    height: 48px;
    flex-shrink: 0;
    opacity: .2;
    transition: all .2s;
}
.input.input.input {
    height: 48px;
    z-index: v-bind("isOpen ? 99999 : 0");
    position: fixed;
    &> * {
        z-index: 99999;
    }
    transition: width .2s, top .3s, left .4s;
}
</style>
